import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdminArchiveBranchRequest,
  AdminCreateBranchRequest,
  AdminPatchBranchRequest,
} from '@naya/shared'
import { adminApi } from '../adminApi.js'
import type { BranchListResponse, BranchRow } from '../types.js'
import { tenantDetailKey } from './useTenants.js'

export const branchesKey = (tenantId: string, includeArchived: boolean) =>
  ['admin', 'tenants', tenantId, 'branches', { includeArchived }] as const

interface BranchResponse {
  branch: BranchRow
}

export function useBranches(tenantId: string, includeArchived: boolean) {
  return useQuery({
    queryKey: branchesKey(tenantId, includeArchived),
    queryFn: () =>
      adminApi.get<BranchListResponse>(
        `/tenants/${tenantId}/branches${includeArchived ? '?include_archived=true' : ''}`,
      ),
  })
}

/** Invalidates both archived/non-archived branch lists plus the tenant detail. */
function invalidateBranches(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: string,
) {
  void qc.invalidateQueries({
    queryKey: ['admin', 'tenants', tenantId, 'branches'],
  })
  void qc.invalidateQueries({ queryKey: tenantDetailKey(tenantId) })
}

export function useCreateBranch(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AdminCreateBranchRequest) =>
      adminApi.post<BranchResponse>(`/tenants/${tenantId}/branches`, input),
    onSuccess: () => invalidateBranches(qc, tenantId),
  })
}

export function usePatchBranch(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { branchId: string } & AdminPatchBranchRequest) => {
      // Slice 4b — forward lat/lng alongside name. Undefined keys are
      // stripped by the JSON serializer; `null` is preserved (= "clear coord").
      const { branchId, ...patch } = vars
      return adminApi.patch<BranchResponse>(
        `/tenants/${tenantId}/branches/${branchId}`,
        patch,
      )
    },
    onSuccess: () => invalidateBranches(qc, tenantId),
  })
}

export function useArchiveBranch(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { branchId: string } & AdminArchiveBranchRequest) =>
      adminApi.post<BranchResponse>(
        `/tenants/${tenantId}/branches/${vars.branchId}/archive`,
        { reason: vars.reason },
      ),
    onSuccess: () => invalidateBranches(qc, tenantId),
  })
}

export function useUnarchiveBranch(tenantId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (branchId: string) =>
      adminApi.post<BranchResponse>(
        `/tenants/${tenantId}/branches/${branchId}/unarchive`,
      ),
    onSuccess: () => invalidateBranches(qc, tenantId),
  })
}
